import type { Analytics } from 'firebase/analytics';
import { app, isConfigured } from './firebase';

/**
 * Google Analytics via Firebase. Page views are automatic; `track` adds the few
 * actions worth counting. Never send amounts, names or descriptions.
 *
 * The SDK is imported lazily so it stays out of the first paint, and it stays
 * off in dev and wherever the browser can't support it (some in-app browsers).
 */

type Params = Record<string, string | number | boolean>;

let ready: Promise<Analytics | null> | undefined;

function analytics(): Promise<Analytics | null> {
  ready ??= (async () => {
    if (!isConfigured || !import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || import.meta.env.DEV) return null;
    const { getAnalytics, isSupported } = await import('firebase/analytics');
    return (await isSupported()) ? getAnalytics(app) : null;
  })().catch(() => null);
  return ready;
}

export function track(event: string, params?: Params): void {
  void analytics().then(async (a) => {
    if (!a) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(a, event, params);
  }).catch(() => { /* analytics must never break the app */ });
}

/** Ties events to the signed-in account (a Firebase uid — not personal data). */
export function identify(uid: string | null): void {
  void analytics().then(async (a) => {
    if (!a) return;
    const { setUserId } = await import('firebase/analytics');
    setUserId(a, uid);
  }).catch(() => {});
}
